/**
 * 管理后台画布预览：节点详情面板。
 *
 * 预览画布复用真实节点组件渲染（不可点击），详情统一从左侧节点清单进入，
 * 这里只负责把节点 data 的可读字段摊开展示。
 */
import { useMemo } from 'react';
import { ChevronLeft } from 'lucide-react';
import { resolveBrowserMediaUrl } from '../../../utils/storageUrl';
import { nodeTypeLabel, type PreviewNode } from './previewNode';

export function NodeDetailPanel({ node, onClose }: { node: PreviewNode; onClose: () => void }) {
  const data = node.data;
  const visual = useMemo(() => nodeTypeLabel(node.type), [node.type]);

  const renderDetail = () => {
    switch (node.type) {
      case 'text':
        return (
          <DetailRow label="内容">
            <p className="whitespace-pre-wrap break-words text-sm text-slate-700">{data.content || '(空)'}</p>
          </DetailRow>
        );
      case 'image':
      case 'video': {
        const url = data.imageUrl || data.videoUrl;
        const resolved = url && !url.startsWith('blob:') ? resolveBrowserMediaUrl(url) : null;
        return (
          <>
            {resolved && (
              <DetailRow label={node.type === 'image' ? '图片' : '视频'}>
                {node.type === 'image' ? (
                  <img src={resolved} alt="" className="w-full rounded-lg" />
                ) : (
                  <video src={resolved} controls className="w-full rounded-lg" />
                )}
              </DetailRow>
            )}
            {data.prompt && <DetailRow label="提示词"><p className="text-sm text-slate-700">{data.prompt}</p></DetailRow>}
            {data.modelLabel && <DetailRow label="模型"><span className="text-sm text-slate-600">{data.modelLabel}</span></DetailRow>}
            {data.status && <DetailRow label="状态"><span className="text-sm">{data.status}</span></DetailRow>}
            {data.aspectRatio && <DetailRow label="比例"><span className="text-sm">{data.aspectRatio}</span></DetailRow>}
            {typeof data.refNo === 'number' && <DetailRow label="图号"><span className="text-sm">#{data.refNo}</span></DetailRow>}
          </>
        );
      }
      case 'image_group': {
        const urls: (string | null)[] = data.imageUrls || [];
        return (
          <>
            <DetailRow label="图片数量"><span className="text-sm">{urls.length}</span></DetailRow>
            <DetailRow label="图片列表">
              <div className="grid grid-cols-2 gap-2">
                {urls.map((u, i) => {
                  const resolved = u && !u.startsWith('blob:') ? resolveBrowserMediaUrl(u) : null;
                  return resolved ? (
                    <img key={i} src={resolved} alt="" className="w-full rounded-lg" loading="lazy" />
                  ) : (
                    <div key={i} className="aspect-square rounded-lg bg-slate-100 flex items-center justify-center text-xs text-slate-400">无图</div>
                  );
                })}
              </div>
            </DetailRow>
            {data.modelLabel && <DetailRow label="模型"><span className="text-sm">{data.modelLabel}</span></DetailRow>}
          </>
        );
      }
      case 'video_group': {
        const urls: string[] = data.videoUrls || [];
        return (
          <>
            <DetailRow label="视频数量"><span className="text-sm">{urls.length}</span></DetailRow>
            {urls.map((u, i) => {
              const resolved = u && !u.startsWith('blob:') ? resolveBrowserMediaUrl(u) : null;
              return resolved ? (
                <DetailRow key={i} label={`视频 ${i + 1}`}>
                  <video src={resolved} controls className="w-full rounded-lg" />
                </DetailRow>
              ) : null;
            })}
          </>
        );
      }
      case 'audio':
        return (
          <>
            <DetailRow label="文件名"><span className="text-sm">{data.fileName || '--'}</span></DetailRow>
            <DetailRow label="时长"><span className="text-sm">{data.durationSec ?? '--'}s</span></DetailRow>
            <DetailRow label="大小"><span className="text-sm">{data.fileSize ?? '--'} bytes</span></DetailRow>
          </>
        );
      case 'generate_image':
      case 'generate_video':
        return (
          <>
            {data.prompt && <DetailRow label="提示词"><p className="text-sm text-slate-700">{data.prompt}</p></DetailRow>}
            {data.modelId && <DetailRow label="模型 ID"><span className="text-sm font-mono">{data.modelId}</span></DetailRow>}
            {data.mode && <DetailRow label="模式"><span className="text-sm">{data.mode}</span></DetailRow>}
            {data.aspectRatio && <DetailRow label="比例"><span className="text-sm">{data.aspectRatio}</span></DetailRow>}
            {data.quality && <DetailRow label="质量"><span className="text-sm">{data.quality}</span></DetailRow>}
            {data.resolution && <DetailRow label="分辨率"><span className="text-sm">{data.resolution}</span></DetailRow>}
            {data.duration && <DetailRow label="时长"><span className="text-sm">{data.duration}s</span></DetailRow>}
            {data.count && <DetailRow label="数量"><span className="text-sm">{data.count}</span></DetailRow>}
          </>
        );
      case 'llm':
        return (
          <>
            {data.input && <DetailRow label="输入"><p className="whitespace-pre-wrap text-sm text-slate-700">{data.input}</p></DetailRow>}
            {data.output && <DetailRow label="输出"><p className="whitespace-pre-wrap text-sm text-slate-700">{data.output}</p></DetailRow>}
            {data.errorMessage && <DetailRow label="错误"><p className="text-sm text-red-500">{data.errorMessage}</p></DetailRow>}
          </>
        );
      case 'agent':
        return (
          <>
            {data.instruction && <DetailRow label="指令"><p className="text-sm text-slate-700">{data.instruction}</p></DetailRow>}
            {data.status && <DetailRow label="状态"><span className="text-sm">{data.status}</span></DetailRow>}
            {data.stylePrefix && <DetailRow label="风格前缀"><p className="text-sm text-slate-700">{data.stylePrefix}</p></DetailRow>}
            {data.items && data.items.length > 0 && (
              <DetailRow label={`计划项 (${data.items.length})`}>
                <div className="space-y-1.5">
                  {data.items.map((item: { label?: string; prompt?: string }, i: number) => (
                    <div key={i} className="rounded-lg bg-slate-50 p-2">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-medium text-slate-600">#{i + 1} {item.label}</span>
                        {data.itemStatuses?.[i] && <span className="text-[10px] text-slate-400">{data.itemStatuses[i]}</span>}
                      </div>
                      <p className="mt-0.5 text-xs text-slate-500">{item.prompt}</p>
                    </div>
                  ))}
                </div>
              </DetailRow>
            )}
          </>
        );
      case 'style_card':
        return (
          <>
            {data.coverUrl && <DetailRow label="封面"><img src={resolveBrowserMediaUrl(data.coverUrl)} alt="" className="w-full rounded-lg" /></DetailRow>}
            <DetailRow label="名称"><span className="text-sm">{data.name}</span></DetailRow>
            {data.prompt && <DetailRow label="提示词"><p className="text-sm text-slate-700">{data.prompt}</p></DetailRow>}
            {data.category && <DetailRow label="分类"><span className="text-sm">{data.category}</span></DetailRow>}
          </>
        );
      case 'group':
        return <DetailRow label="标签"><span className="text-sm">{data.label || data._title || '(无)'}</span></DetailRow>;
      default:
        return null;
    }
  };

  return (
    <div className="flex h-full w-full flex-col bg-white">
      <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
        <div className="flex items-center gap-2">
          <button onClick={onClose} className="flex items-center gap-1 text-sm text-slate-400 hover:text-slate-600 transition-colors">
            <ChevronLeft className="h-4 w-4" />返回
          </button>
          <span className="text-slate-200">|</span>
          <h4 className="text-sm font-semibold text-slate-700">{visual}</h4>
        </div>
      </div>
      <div className="flex-1 overflow-auto p-4 space-y-3">
        <div className="grid grid-cols-2 gap-2 rounded-lg bg-slate-50 p-3 text-xs">
          <div><span className="text-slate-400">节点 ID: </span><span className="font-mono text-slate-600">{node.id}</span></div>
          <div><span className="text-slate-400">位置: </span><span className="text-slate-600">({Math.round(node.x)}, {Math.round(node.y)})</span></div>
          <div><span className="text-slate-400">尺寸: </span><span className="text-slate-600">{node.width} x {node.height}</span></div>
          <div><span className="text-slate-400">层级: </span><span className="text-slate-600">{node.zIndex}</span></div>
        </div>
        {renderDetail()}
      </div>
    </div>
  );
}

function DetailRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="mb-1 text-xs font-medium text-slate-400">{label}</p>
      <div>{children}</div>
    </div>
  );
}
